'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { authClient } from '@/lib/auth-client';

interface FormData {
  email: string;
}

export default function ForgotPasswordForm() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    setAuthError('');

    const redirectTo =
      (typeof window !== 'undefined' && `${window.location.origin}/reset-password`) || undefined;

    const { error } = await authClient.requestPasswordReset({
      email: data.email,
      redirectTo,
    });

    // Always show success — never reveal whether the account exists.
    if (error && error.status !== 200 && error.status !== 400 && error.status !== 404) {
      setAuthError(error.message ?? 'Could not send the reset email. Try again.');
      setSubmitting(false);
      return;
    }

    setSent(true);
    setSubmitting(false);
  };

  if (sent) {
    return (
      <div className="space-y-5">
        <div className="flex items-start gap-3 bg-success-muted/30 border border-success/30 rounded-lg p-4">
          <CheckCircle2 size={18} className="text-success mt-0.5 shrink-0" />
          <div>
            <h2 className="text-base font-semibold text-foreground mb-1">Check your inbox</h2>
            <p className="text-sm text-muted-foreground">
              If an account exists for <strong>{getValues('email')}</strong>, we&rsquo;ve sent a
              password reset link. The link expires in 1 hour.
            </p>
          </div>
        </div>
        <Link
          href="/login"
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <ArrowLeft size={14} />
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-1">Forgot your password?</h2>
        <p className="text-sm text-muted-foreground">
          Enter the email tied to your account and we&rsquo;ll send you a link to reset it.
        </p>
      </div>

      {authError && (
        <div className="flex items-start gap-2.5 bg-danger/10 border border-danger/30 rounded-lg p-3 animate-slide-up">
          <AlertCircle size={14} className="text-danger mt-0.5 shrink-0" />
          <p className="text-xs text-danger">{authError}</p>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
          Work Email <span className="text-danger">*</span>
        </label>
        <input
          type="email"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email address',
            },
          })}
          placeholder="you@whitestoneinfotech.com"
          className={`input-base ${errors.email ? 'input-error' : ''}`}
          autoComplete="email"
          autoFocus
        />
        {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary w-full justify-center text-base py-3 gap-2"
      >
        {submitting ? (
          <>
            <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            Sending link...
          </>
        ) : (
          <>
            <Send size={16} />
            Send reset link
          </>
        )}
      </button>

      <Link
        href="/login"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} />
        Back to sign in
      </Link>
    </form>
  );
}
